/**
 * Global console override that suppresses browser console output in non-development environments.
 *
 * In deployed environments (staging, production), native console methods are replaced with no-ops
 * to keep the browser DevTools clean. In development (`import.meta.env.DEV`) and during tests
 * (`import.meta.env.VITEST`), console output is left untouched.
 *
 * Call this once during app bootstrap, after Sentry has been initialized, so that
 * Sentry's breadcrumb wrappers are already in place before we silence the output.
 */

/** Console methods that will be silenced in non-dev environments. */
type TConsoleMethod = 'log' | 'debug' | 'info' | 'warn' | 'error' | 'trace';

const METHODS_TO_OVERRIDE: TConsoleMethod[] = [
  'log',
  'debug',
  'info',
  'warn',
  'error',
  'trace',
];

const noop = () => {};

/**
 * Whether the current environment should display console output.
 *
 * Returns `true` for local development (`yarn dev`) and test runners (Vitest).
 *
 * @returns {boolean} `true` if console output should be visible
 */
export function isConsoleEnabled(): boolean {
  return import.meta.env.DEV || Boolean(import.meta.env.VITEST);
}

/**
 * Replaces native console methods with no-ops when running outside development.
 *
 * Should be called once in `main.tsx` after `initSentry()` so Sentry's
 * breadcrumb integrations are wired up before the methods are silenced.
 *
 * In development and test environments this function is a no-op.
 */
export function initConsoleOverride(): void {
  if (isConsoleEnabled()) {
    return;
  }

  for (const method of METHODS_TO_OVERRIDE) {
    // eslint-disable-next-line no-console -- reason: This IS the console override utility; it must access console to silence it
    console[method] = noop as (typeof console)[typeof method];
  }
}
